import React, { useState } from 'react';
import { useHistory } from "react-router-dom";
import Routes from '../routes';
import { PrimaryButton } from './Common';
import PasswordField from "./Common/PasswordInput";
import "./ResetPassword.css";
import toast from 'react-hot-toast';
import { Box, Container, Grid, List, ListItem, Stack, Typography } from '@mui/material';

const EXPIRED_LINK_MESSAGE = "Password link is expired, please try again with a new password reset email.";
const BAD_LINK_MESSAGE = "Error authenticating password reset link. Please try again with a new password reset email.";
const SERVER_ERROR_MESSAGE = "Something went wrong changing your password. Please try again.";

const ResetPassword = (props) => {
    // console.log(props.match.params.guid)
    const history = useHistory();
    const [password, setPassword] = useState(null);
    const [confirmPassword, setConfirmPassword] = useState(null);
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState(null);
    // AB#17039: in-flight guard so a second submit cannot raise a second toast.
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateNewPassword = async () => {
        const changePasswordBody = {
            GUID: props.match.params.guid,
            NewPassword: password
        };
        const response = await fetch(Routes.Account.ValidatePasswordChange(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(changePasswordBody)
        });

        let body = null;
        try {
            body = await response.json();
        } catch {
            body = null;
        }

        return { status: response.status, body };
    };

    const validate = async () => {
        let isValid = true;
        setPasswordError(null);
        setConfirmPasswordError(null);

        if (!password) {
            setPasswordError("Password cannot be empty");
            isValid = false;
        }

        if (password !== confirmPassword) {
            setConfirmPasswordError("Passwords do not match");
            isValid = false;
        }

        if (password && password.length < 8) {
            setPasswordError("Password must be at least 8 characters");
            isValid = false;
        }

        if (password && /[A-Z]/.test(password) === false) {
            setPasswordError("Password must have at least 1 capital letter");
            isValid = false;
        }

        if (password && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password) === false) {
            setPasswordError("Password must have at least 1 special character");
            isValid = false;
        }

        const { status, body } = await validateNewPassword();

        if (password && status === 400) {
            if (body === "Password Reset GUID incorrect") {
                setPasswordError(null);
                setConfirmPasswordError(null);
                toast.error(BAD_LINK_MESSAGE, { duration: 6000, id: "reset-password-bad-link" });
            } else {
                setPasswordError("New password cannot be the same as your current password");
            }
            isValid = false;
        }
        else if (password && status >= 500) {
            // AB#17039: a server fault is not an expired link — say what actually happened.
            setPasswordError(null);
            setConfirmPasswordError(null);
            toast.error(SERVER_ERROR_MESSAGE, { duration: 6000, id: "reset-password-server-error" });
            isValid = false;
        }

        return isValid;
    };

    const changePassword = async () => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        const isValid = await validate();

        if (!isValid) {
            setIsSubmitting(false);
            return;
        }

        const changePasswordBody = {
            GUID: props.match.params.guid,
            NewPassword: password
        };

        const response = await fetch(Routes.Account.ChangePassword(), {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(changePasswordBody)
        });

        let res = null;
        try {
            res = await response.json();
        } catch {
            res = null;
        }

        if (response.status === 200) {
            toast.success("The password has been successfully changed.", { duration: 6000, id: "reset-password-success" });
            return setTimeout(() => history.push("/Login"), 6000);
        } else if (response.status === 400) {
            if (res === "Password Reset Link Expired") {
                toast.error(EXPIRED_LINK_MESSAGE, { duration: 6000, id: "reset-password-expired" });
            } else if (res === "Password Reset GUID incorrect" || res === "Error Confirming Account for reset") {
                toast.error(BAD_LINK_MESSAGE, { duration: 6000, id: "reset-password-bad-link" });
            } else {
                toast.error("Error Resetting password.", { duration: 6000, id: "reset-password-error" });
            }
        } else {
            toast.error(SERVER_ERROR_MESSAGE, { duration: 6000, id: "reset-password-server-error" });
        }

        setIsSubmitting(false);
    };

    const handleKeyDown = (e) => {
        if (e.keyCode === 13) {
            return changePassword();
        }
    };

    return (
        <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: '100vh', px: 2 }}
        >
            <Grid item xs={12} sm={8} md={6} lg={4}>
                <Stack spacing={4}>
                    <div className="imageHeader">
                        <img
                            className="LoginLogo bottomMargin"
                            src='/images/logo_2025.svg'
                            alt="logo"
                        />
                    </div>
                    <div className="resetInfoContainer">
                        <p className="changeHeader">Change Password</p>
                        <br />
                        <p className="regularText">In order to protect your account, make sure you choose a unique password that: </p>
                        <ul className='ulContainer'>
                            <li>Have at least 8 characters</li>
                            <li>Have at least 1 capital letter</li>
                            <li>Have at least 1 special character</li>
                        </ul>
                    </div>
                    <Stack spacing={2}>
                        <PasswordField
                            required
                            fullWidth
                            id="new-password"
                            label="New Password"
                            value={password}
                            error={!!passwordError}
                            helperText={passwordError}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                        <PasswordField
                            required
                            fullWidth
                            id="confirm-password"
                            label="Confirm New Password"
                            value={confirmPassword}
                            error={!!confirmPasswordError}
                            helperText={confirmPasswordError}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                    </Stack>
                    <PrimaryButton
                        fullWidth
                        useLargeButton
                        buttonText="Change Password"
                        disabled={!password || !confirmPassword || isSubmitting}
                        onClick={changePassword}
                    />
                </Stack>
            </Grid>
        </Grid>
    );
};

export default ResetPassword;
